import { ClaimStatusSummary, PolicyCoverage } from './ClaimData'
import { ClaimRecordStatus } from './ClaimRecord'
import { Price } from './Price'
import { ProductInsuranceType } from './Product'

export type GetQuoteArgs = {
  claimRecordId: string
  termsAndConditionsAccepted: boolean
}

export type ClaimedItem = {
  cartProductBundleId: string
  customerAmount: Price
  insuranceRequestType: ProductInsuranceType
}

export type InsuranceTransactionType = 'COMPOUND' | 'DISCOUNT_ONLY' | 'INSURANCE_COVERED'

export type QuoteRefuseReasonType =
  | 'SESSION_TIMEOUT'
  | 'CUSTOMER_LOGOUT'
  | 'CART_UPDATE'
  | 'CART_DELETED'
  | 'CUSTOMER_INITIATED'
  | 'SALE_PRICE_BETTER_THAN_INSURANCE_PRICE'
  | 'CLEANUP_UNRESOLVED'

export type QuoteItem = {
  claimedItems: ClaimedItem[]
  customerAmount: Price
  requestedAmount: Price
  insuranceTransactionType: InsuranceTransactionType
  refuseQuoteReason?: QuoteRefuseReasonType
}

export type QuoteData = {
  status: QuoteStatus
  customerFacingStatus?: QuoteCustomerFacingStatus
  primaryCoverage: PolicyCoverage
  shippingCosts: Price
  quoteConfirmedAmount: Price

  // if status is ERROR
  errorType?: QuoteErrorType
  errorCode?: QuoteErrorCode
  errorMessage?: string

  claimedItems?: ClaimedItem[]
  insuranceTransactionType?: InsuranceTransactionType
  refuseQuoteReason?: QuoteRefuseReasonType

  childQuotes?: QuoteItem[]
}

export const enum QuoteStatus {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  DELETED = 'DELETED',
  SUBMITTED = 'SUBMITTED',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED',
  ERROR = 'ERROR',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  RETRY = 'RETRY',
  RETRY_CANCELLED = 'RETRY_CANCELLED',
  CX_ACCEPTED = 'CX_ACCEPTED',
  CX_ACCEPT_FAILED = 'CX_ACCEPT_FAILED',
  CX_REFUSED = 'CX_REFUSED',
  CX_REFUSE_FAILED = 'CX_REFUSE_FAILED',
  SYSTEM_REFUSED = 'SYSTEM_REFUSED',
  CLAIM_NULLIFIED = 'CLAIM_NULLIFIED',
  CLOSED = 'CLOSED',
  NONE = 'NONE',
}

export const enum QuoteCustomerFacingStatus {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  DELETED = 'DELETED',
  SUBMITTED = 'SUBMITTED',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED',
  ERROR = 'ERROR',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  RETRY = 'RETRY',
  RETRY_CANCELLED = 'RETRY_CANCELLED',
  CX_ACCEPTED = 'CX_ACCEPTED',
  CX_ACCEPT_FAILED = 'CX_ACCEPT_FAILED',
  CX_REFUSED = 'CX_REFUSED',
  CX_REFUSE_FAILED = 'CX_REFUSE_FAILED',
  SYSTEM_REFUSED = 'SYSTEM_REFUSED',
  CLAIM_NULLIFIED = 'CLAIM_NULLIFIED',
  NONE = 'NONE',
}

export type QuoteErrorData = {
  errorType?: QuoteErrorType
  errorCode?: QuoteErrorCode
  errorMessage?: string
}

export const enum QuoteErrorType {
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  COMMUNICATION_ERROR = 'COMMUNICATION_ERROR',
  CUSTOMER_FACING_ERROR = 'CUSTOMER_FACING_ERROR',
  RESTRICTED_PRODUCT_ERROR = 'RESTRICTED_PRODUCT_ERROR',
  TECHNICAL_ERROR = 'TECHNICAL_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
}

export const enum QuoteErrorCode {
  BENEFICIARY_NOT_FOUND = 'BENEFICIARY_NOT_FOUND',
  INSUFFICIENT_BALANCE = 'INSUFFICIENT_BALANCE',
  INSURER_OFFLINE = 'INSURER_OFFLINE',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

export type QuoteStatusSummary = {
  status: QuoteStatus
  customerFacingStatus?: QuoteCustomerFacingStatus
  errorType?: QuoteErrorType
  errorCode?: QuoteErrorCode
  errorMessage?: string
  lastQuote?: boolean
  quoteId?: string
  claimRecordStatus?: ClaimRecordStatus
}

export type StatusSummary = {
  quote: QuoteStatusSummary
  claim: ClaimStatusSummary
  quoteData?: QuoteStatusSummary
}

export const enum QuoteStep {
  REQUEST = 'REQUEST',
  SUBMIT = 'SUBMIT',
  OFFLINE = 'OFFLINE',
  ACCEPTED = 'ACCEPTED',
  NONE = 'NONE',
}
